
import { useParams } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import DashboardSidebar from "@/components/DashboardSidebar";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { Clock } from "lucide-react";

interface MaintenanceAppointment {
  id: string;
  requested_date: string;
  requested_time: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'cancelled';
  description: string | null;
  created_at: string;
  mechanic_notes: string | null;
  pickup_requested: boolean;
  pickup_cost: number | null;
  washing_requested: boolean;
  detailing_requested: boolean;
}

const MaintenanceOverviewPage = () => {
  const { id } = useParams();

  const { data: appointments } = useQuery({
    queryKey: ["maintenance-appointments", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vehicle_maintenance")
        .select("*")
        .eq("vehicle_id", id)
        .order("requested_date", { ascending: false });

      if (error) throw error;
      return data as MaintenanceAppointment[];
    },
  });

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "pending":
        return "In behandeling";
      case "approved":
        return "Goedgekeurd";
      case "rejected":
        return "Afgewezen";
      case "completed":
        return "Voltooid";
      case "cancelled":
        return "Geannuleerd";
      default:
        return status;
    }
  };

  if (!id) return null;

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <DashboardSidebar />
        <main className="flex-1 p-8 bg-gray-50">
          <div className="max-w-5xl mx-auto space-y-8">
            <h1 className="text-2xl font-semibold">Onderhoud Historie</h1>
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-[#B1A06D]" />
                  <CardTitle className="text-xl font-semibold">Geplande & historische afspraken</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {appointments?.map((appointment) => (
                    <div
                      key={appointment.id}
                      className="flex items-center justify-between rounded-lg border p-4"
                    >
                      <div className="space-y-1">
                        <p className="font-medium">
                          {format(new Date(appointment.requested_date), "d MMMM yyyy", { locale: nl })} -{" "}
                          {appointment.requested_time}
                        </p>
                        <p className="text-sm text-gray-500">
                          {appointment.description || "Regulier onderhoud"}
                        </p>
                        {appointment.mechanic_notes && (
                          <p className="text-sm text-gray-500">
                            <span className="font-medium">Notitie monteur:</span> {appointment.mechanic_notes}
                          </p>
                        )}
                        <div className="space-y-1 mt-2">
                          {appointment.pickup_requested && (
                            <p className="text-sm text-gray-500">
                              <span className="font-medium">Extra service:</span> Ophaalservice
                              {appointment.pickup_cost && ` (€${appointment.pickup_cost.toFixed(2)})`}
                            </p>
                          )}
                          {appointment.washing_requested && (
                            <p className="text-sm text-gray-500">
                              <span className="font-medium">Extra service:</span> Auto wassen
                            </p>
                          )}
                          {appointment.detailing_requested && (
                            <p className="text-sm text-gray-500">
                              <span className="font-medium">Extra service:</span> Auto detailen
                            </p>
                          )}
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeColor(
                          appointment.status
                        )}`}
                      >
                        {getStatusText(appointment.status)}
                      </span>
                    </div>
                  ))}
                  {appointments?.length === 0 && (
                    <p className="text-center text-gray-500 py-4">
                      Nog geen onderhoudsafspraken gepland
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default MaintenanceOverviewPage;
